import { useMemo, useState } from 'react';
import { useAdminMessages, useHandleMessage } from '../useAdmin';
import type { AdminMessageRow, InboundKind, InboundStatus, ListMessagesParams } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminMessages.css';

const PAGE_SIZE = 30;

const KIND_OPTIONS: { value: '' | InboundKind; label: string }[] = [
  { value: '', label: 'Any kind' },
  { value: 'feature', label: 'Feature requests' },
  { value: 'bug', label: 'Bug reports' },
  { value: 'contact', label: 'General' },
];

const STATUS_OPTIONS: { value: InboundStatus | 'all'; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'handled', label: 'Handled' },
  { value: 'spam', label: 'Spam' },
  { value: 'wontfix', label: "Won't fix" },
  { value: 'all', label: 'All' },
];

/**
 * Admin inbox for inbound contact / feature / bug messages.
 *
 * Default view is `status=open`. Each row exposes inline triage
 * actions (mark handled / spam / won't fix). Optional note rides
 * along so the admin team can record the resolution context.
 */
export function AdminMessages(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<InboundStatus | 'all'>('open');
  const [kind, setKind] = useState<'' | InboundKind>('');
  const [page, setPage] = useState(0);

  const params = useMemo<ListMessagesParams>(
    () => ({
      status: statusFilter,
      kind: kind || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [statusFilter, kind, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminMessages(params);
  const handle = useHandleMessage();

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <section className="bl-admin-msgs" aria-label="Inbound messages">
      <header className="bl-admin-msgs__header">
        <div>
          <h2>Inbox</h2>
          <p className="bl-admin-msgs__sub">
            Feature requests, bug reports, and contact messages from users — both signed-in and
            anonymous. Triage to keep the queue tidy.
          </p>
        </div>
        <p className="bl-admin-msgs__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'Nothing to triage'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form className="bl-admin-msgs__filters" onSubmit={(e) => e.preventDefault()} role="search">
        <label className="bl-admin-msgs__field">
          <span className="bl-admin-msgs__field-label">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => reset(setStatusFilter)(e.target.value as InboundStatus | 'all')}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-msgs__field">
          <span className="bl-admin-msgs__field-label">Kind</span>
          <select value={kind} onChange={(e) => reset(setKind)(e.target.value as '' | InboundKind)}>
            {KIND_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </form>

      {(isError || handle.error) && (
        <p className="bl-admin-msgs__error" role="alert">
          {handle.error instanceof AuthApiError
            ? handle.error.detail || handle.error.message
            : (error || handle.error) instanceof Error
              ? ((error || handle.error) as Error).message
              : 'Could not load.'}
        </p>
      )}

      <ul className="bl-admin-msgs__list">
        {data?.rows.map((row) => (
          <MessageEntry
            key={row.id}
            row={row}
            busy={handle.isPending}
            onHandle={(status, note) => handle.mutate({ id: row.id, status, note })}
          />
        ))}
        {!isLoading && data?.rows.length === 0 && (
          <li className="bl-admin-msgs__empty">
            {statusFilter === 'open'
              ? 'Inbox zero. Nothing open right now.'
              : 'No messages match the current filters.'}
          </li>
        )}
      </ul>

      <nav className="bl-admin-msgs__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-msgs__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function MessageEntry({
  row,
  onHandle,
  busy,
}: {
  row: AdminMessageRow;
  onHandle: (status: InboundStatus, note?: string) => void;
  busy: boolean;
}): JSX.Element {
  const isOpen = row.status === 'open';
  return (
    <li className="bl-admin-msgs__entry" data-kind={row.kind} data-status={row.status}>
      <div className="bl-admin-msgs__entry-head">
        <span className={`bl-admin-msgs__kind bl-admin-msgs__kind--${row.kind}`}>{row.kind}</span>
        <span className="bl-admin-msgs__subject">{row.subject}</span>
        {row.status !== 'open' && (
          <span className="bl-admin-msgs__pill">
            {row.status} · {row.handled_by_email || '(unknown)'}
          </span>
        )}
        <time className="bl-admin-msgs__when" dateTime={row.created_at}>
          {formatDate(row.created_at)}
        </time>
      </div>
      <p className="bl-admin-msgs__from">
        from{' '}
        {row.sender_username
          ? `@${row.sender_username}`
          : row.sender_email || row.sender_name || '(anonymous)'}
        {row.sender_email && (row.sender_username || row.sender_name) && ` · ${row.sender_email}`}
      </p>
      <pre className="bl-admin-msgs__body">{row.body}</pre>
      {row.handled_note && <p className="bl-admin-msgs__note">Handled note: {row.handled_note}</p>}
      {isOpen && (
        <div className="bl-admin-msgs__actions">
          <button
            type="button"
            onClick={() => {
              const note = window.prompt('Resolution note (optional):', '');
              if (note === null) return;
              onHandle('handled', note.trim() || undefined);
            }}
            disabled={busy}
            className="bl-admin-msgs__action--primary"
          >
            Mark handled
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm("Mark as won't-fix? Sender won't be replied to."))
                onHandle('wontfix');
            }}
            disabled={busy}
          >
            Won&rsquo;t fix
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Mark as spam?')) onHandle('spam');
            }}
            disabled={busy}
          >
            Spam
          </button>
        </div>
      )}
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
